import { PaletteMode } from '@mui/material';
import { purple } from '@mui/material/colors';
// https://github.com/mui/material-ui/issues/14185#issuecomment-717401315
// Reported bug: https://github.com/mui/material-ui/issues/33054

interface AvailableBrandColors {
  brandWhite?: string;
  brandBlue?: string;
  brandYellow?: string;
  brandGreen?: string;
  brandRed?: string;
  brandGray?: string;
  brandLightGray?: string;
  brandBlueGray?: string;
}

interface BrandColorOverrides {
  brandWhite?: true;
  brandBlue?: true;
  brandYellow?: true;
  brandGreen?: true;
  brandRed?: true;
  brandGray?: true;
  brandLightGray?: true;
  brandBlueGray?: true;
}

// Declare brand colors to use in components as default ones
declare module '@mui/material/styles' {
  interface Palette {
    brandBlue: string;
    brandYellow: string;
    brandGreen: string;
    brandRed: string;
    brandGray: string;
    brandLightGray: string;
    brandBlueGray: string;
    porcelain: string;
    linkWater: string;
    navyBlue: string;
    resolutionBlue: string;
    blueGray: string;
    waterloo: string;
    pigPink: string;
    thunderbird: string;
    white: string;
    kleinBlue: string;
    titanWhite: string;
    darkBlue: string;
    gamboge: string;
    red: string;
    sand: string;
    whiteLilac: string;
    silver: string;
    comet: string;
    darkerBlue: string;
    flamingo: string;
    dodgerBlue: string;
    lightGray: string;
  }
  interface PaletteOptions {
    brandBlue: string;
    brandYellow: string;
    brandGreen: string;
    brandRed: string;
    brandGray: string;
    brandLightGray: string;
    brandBlueGray: string;
    porcelain: string;
    linkWater: string;
    navyBlue: string;
    resolutionBlue: string;
    blueGray: string;
    waterloo: string;
    pigPink: string;
    thunderbird: string;
    white: string;
    kleinBlue: string;
    titanWhite: string;
    darkBlue: string;
    gamboge: string;
    red: string;
    sand: string;
    whiteLilac: string;
    silver: string;
    comet: string;
    darkerBlue: string;
    flamingo: string;
    dodgerBlue: string;
    lightGray: string;
  }

  interface ThemeOptions {
    borderRadius: {
      small: string;
      default: string;
    };
  }

  interface Theme {
    borderRadius: {
      small: string;
      default: string;
    };
  }

  interface Palette extends AvailableBrandColors {}
  interface PaletteOptions extends AvailableBrandColors {}
}

// declare module "@mui/material/Button" { interface ButtonPropsColorOverrides extends BrandColorOverrides {} }
// declare module "@mui/material/IconButton" { interface IconButtonPropsColorOverrides extends BrandColorOverrides {} }
declare module '@mui/material/Card' {
  interface CardPropsColorOverrides extends BrandColorOverrides {}
}
declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides extends BrandColorOverrides {}
}
declare module '@mui/material/Typography' {
  interface TypographyPropsColorOverrides extends BrandColorOverrides {}
}

const lightThemeColors = {
  brandWhite: '#FFFFFF',
  brandBlue: '#041DAE',
  brandYellow: '#FBB41D',
  brandGreen: '#037F01',
  brandRed: '#FF3D1D',
  brandGray: '#8C97A1',
  brandLightGray: '#C1C6CC',
  brandBlueGray: '#434E7B',
  porcelain: '#eceeef',
  linkWater: '#DAE1F5',
  navyBlue: '#000A66',
  darkerBlue: '#050099',
  whiteLilac: '#F2F4FA',
  resolutionBlue: '#001485',
  blueGray: '#eaeefa',
  waterloo: '#747a99',
  pigPink: '#fdd8dc',
  thunderbird: '#BB1D12',
  white: '#FFFFFF',
  kleinBlue: '#0318ae',
  titanWhite: '#F0F2FF',
  darkBlue: '#0700C5',
  gamboge: '#e5a00c',
  red: '#e60000',
  sand: '#FCEBCB',
  silver: '#C5C5C5',
  comet: '#5A6081',
  flamingo: '#F04438',
  dodgerBlue: '#5972FF',
  lightGray: '#DAE1F4',
  background: {
    default: '#eaeefa',
  },
  primary: {
    main: '#FBB41D',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#041DAE',
  },
  text: {
    primary: '#000a66',
  },
};

const darkThemeColors = {
  brandWhite: '#4a4747',
  brandBlue: '#041DAE',
  brandYellow: '#FBB41D',
  brandGreen: '#037F01',
  brandRed: '#FF3D1D',
  brandGray: '#8C97A1',
  brandLightGray: '#C1C6CC',
  brandBlueGray: '#434E7B',
  porcelain: '#eceeef',
  linkWater: '#686e7f',
  navyBlue: '#e8eaff',
  blueGray: '#5c5d62',
  waterloo: '#f1f1f4',
  pigPink: '#fdd8dc',
  thunderbird: '#BB1D12',
  white: '#FFFFFF',
  kleinBlue: '#0318ae',
  titanWhite: '#F0F2FF',
  darkBlue: '#e6e5f9',
  gamboge: '#e5a00c',
  red: '#e60000',
  sand: '#191714',
  silver: '#C5C5C5',
  comet: '#5A6081',
  flamingo: '#F04438',
  dodgerBlue: '#5972FF',
  lightGray: '#DAE1F4',
  background: {
    default: '#3f4044',
  },
  primary: {
    main: '#FBB41D',
  },
  secondary: {
    main: '#bad1ff',
  },
  text: {
    primary: '#e8eaff',
  },
};

const getDesignTokens = (mode: PaletteMode) => ({
  typography: {
    fontFamily: 'Avenir Next Cyr, sans-serif',
    root: {
      fontWeight: 500,
    },
    body1: {
      fontWeight: 500,
    },
    caption: {
      fontWeight: 500,
    },
    h2: {
      fontWeight: 600,
      fontSize: 22,
    },
    h3: {
      fontWeight: 500,
      fontSize: 16,
    },
  },
  palette: {
    mode,
    ...(mode === 'light' ? lightThemeColors : darkThemeColors),
  },
  borderRadius: {
    small: '5px',
    default: '10px',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 992,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': {
            paddingBottom: 20,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: mode === 'light' ? '#000a66' : '#e8eaff',
          paddingTop: 0,
          paddingBottom: 0,
        },
        root: {
          color: '#000a66',
          background: mode === 'light' ? '#FFFFFF' : '#4b4b4b',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 8px 24px 0px rgba(0, 14, 92, 0.12)',
          borderRadius: '5px',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: '30px',
          padding: 0,
        },
        content: {
          margin: 0,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        bar: {
          backgroundColor: '#043492FF',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: mode === 'light' ? '#000A66' : '#e8eaff',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          a: {
            textDecoration: 'none',
            '&:hover': {
              backgroundColor: mode === 'light' ? '#DAE1F5' : '#151618',
            },
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',

          '&:hover': {
            textDecoration: 'underline',
            color: 'inherit',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          fontSize: '1rem',
        },
        standardWarning: {
          backgroundColor: '#FBB41D',
          color: 'white',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '5px',
          boxShadow: 'none',
          textTransform: 'none',
          fontSize: 14,
        },
      },
    },
  },
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(50, 71, 92, 0.2), 0px 1px 1px 0px rgba(50, 71, 92, 0.14), 0px 1px 3px 0px rgba(50, 71, 92, 0.12)',
    '0px 3px 1px -2px rgba(50, 71, 92, 0.2), 0px 2px 2px 0px rgba(50, 71, 92, 0.14), 0px 1px 5px 0px rgba(50, 71, 92, 0.12)',
    '0px 4px 8px -4px rgba(50, 71, 92, 0.42)',
    '0px 6px 18px -8px rgba(50, 71, 92, 0.56)',
    '0px 3px 5px -1px rgba(50, 71, 92, 0.2), 0px 5px 8px 0px rgba(50, 71, 92, 0.14), 0px 1px 14px 0px rgba(50, 71, 92, 0.12)',
    '0px 2px 10px 0px rgba(50, 71, 92, 0.1)',
    '0px 4px 5px -2px rgba(50, 71, 92, 0.2), 0px 7px 10px 1px rgba(50, 71, 92, 0.14), 0px 2px 16px 1px rgba(50, 71, 92, 0.12)',
    '0px 5px 5px -3px rgba(50, 71, 92, 0.2), 0px 8px 10px 1px rgba(50, 71, 92, 0.14), 0px 3px 14px 2px rgba(50, 71, 92, 0.12)',
    '0px 5px 6px -3px rgba(50, 71, 92, 0.2), 0px 9px 12px 1px rgba(50, 71, 92, 0.14), 0px 3px 16px 2px rgba(50, 71, 92, 0.12)',
    '0px 6px 6px -3px rgba(50, 71, 92, 0.2), 0px 10px 14px 1px rgba(50, 71, 92, 0.14), 0px 4px 18px 3px rgba(50, 71, 92, 0.12)',
    '0px 6px 7px -4px rgba(50, 71, 92, 0.2), 0px 11px 15px 1px rgba(50, 71, 92, 0.14), 0px 4px 20px 3px rgba(50, 71, 92, 0.12)',
    '0px 7px 8px -4px rgba(50, 71, 92, 0.2), 0px 12px 17px 2px rgba(50, 71, 92, 0.14), 0px 5px 22px 4px rgba(50, 71, 92, 0.12)',
    '0px 7px 8px -4px rgba(50, 71, 92, 0.2), 0px 13px 19px 2px rgba(50, 71, 92, 0.14), 0px 5px 24px 4px rgba(50, 71, 92, 0.12)',
    '0px 7px 9px -4px rgba(50, 71, 92, 0.2), 0px 14px 21px 2px rgba(50, 71, 92, 0.14), 0px 5px 26px 4px rgba(50, 71, 92, 0.12)',
    '0px 8px 9px -5px rgba(50, 71, 92, 0.2), 0px 15px 22px 2px rgba(50, 71, 92, 0.14), 0px 6px 28px 5px rgba(50, 71, 92, 0.12)',
    '0px 8px 10px -5px rgba(50, 71, 92, 0.2), 0px 16px 24px 2px rgba(50, 71, 92, 0.14), 0px 6px 30px 5px rgba(50, 71, 92, 0.12)',
    '0px 8px 11px -5px rgba(50, 71, 92, 0.2), 0px 17px 26px 2px rgba(50, 71, 92, 0.14), 0px 6px 32px 5px rgba(50, 71, 92, 0.12)',
    '0px 9px 11px -5px rgba(50, 71, 92, 0.2), 0px 18px 28px 2px rgba(50, 71, 92, 0.14), 0px 7px 34px 6px rgba(50, 71, 92, 0.12)',
    '0px 9px 12px -6px rgba(50, 71, 92, 0.2), 0px 19px 29px 2px rgba(50, 71, 92, 0.14), 0px 7px 36px 6px rgba(50, 71, 92, 0.12)',
    '0px 10px 13px -6px rgba(50, 71, 92, 0.2), 0px 20px 31px 3px rgba(50, 71, 92, 0.14), 0px 8px 38px 7px rgba(50, 71, 92, 0.12)',
    '0px 10px 13px -6px rgba(50, 71, 92, 0.2), 0px 21px 33px 3px rgba(50, 71, 92, 0.14), 0px 8px 40px 7px rgba(50, 71, 92, 0.12)',
    '0px 10px 14px -6px rgba(50, 71, 92, 0.2), 0px 22px 35px 3px rgba(50, 71, 92, 0.14), 0px 8px 42px 7px rgba(50, 71, 92, 0.12)',
    '0px 11px 14px -7px rgba(50, 71, 92, 0.2), 0px 23px 36px 3px rgba(50, 71, 92, 0.14), 0px 9px 44px 8px rgba(50, 71, 92, 0.12)',
    '0px 11px 15px -7px rgba(50, 71, 92, 0.2), 0px 24px 38px 3px rgba(50, 71, 92, 0.14), 0px 9px 46px 8px rgba(50, 71, 92, 0.12)',
  ],
});

export default getDesignTokens;
